import { useNavigate, useParams } from "react-router-dom";
import ReportTable from "../../components/reports/ReportTable";
import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import {
  downloadGeneratedReport,
  generateReport,
} from "../../sdk/reports/reports";
import { useError } from "../../context/ErrorProvider";
import Pagination from "../../shared/Pagination";
import { Loader, SortAsc, Upload } from "lucide-react";
import { Button } from "antd";
import { formatLabel } from "../../utils/label";
import ReportFilter from "../../components/filter/ReportFilter";
import { downloadExportFile } from "../../utils/export";
import Loading from "../../shared/Loading";

const Report = () => {
  const { name } = useParams();
  const [report, setReport] = useState([]);
  const { showError } = useError();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(50);
  const [startDate, setStartDate] = useState(new Date("2023-11-23"));
  const [endDate, setEndDate] = useState(new Date());
  const [type, setType] = useState("");
  const [branch, setBranch] = useState("");
  const [product, setProduct] = useState("");
  const [status, setStatus] = useState("");
  const navigate = useNavigate();
  const [openFilter, setOpenFilter] = useState(false);

  const toggleFilter = () => {
    setOpenFilter(true);
  };
  const closeFilter = () => {
    setOpenFilter(false);
  };

  const handlePageChange = (page) => {
    setPage(page);
  };

  useEffect(() => {
    let reportDetails = localStorage.getItem("report_filters")
      ? JSON.parse(localStorage.getItem("report_filters"))
      : {};
    setPage(reportDetails?.page);
    setLimit(reportDetails?.limit);
    setStartDate(reportDetails?.startDate);
    setEndDate(reportDetails?.endDate);
    setType(reportDetails?.type);
    setBranch(reportDetails?.branchId);
    setProduct(reportDetails?.productIds);
    setStatus(reportDetails?.status);
  }, []);

  const { isLoading } = useQuery({
    queryKey: [
      "generate reports",
      page,
      limit,
      startDate ? startDate?.toISOString() : null,
      endDate ? endDate?.toISOString() : null,
      type,
      branch,
      product,
      status,
    ],
    queryFn: async () => {
      const response = await generateReport(
        page,
        limit,
        startDate ? startDate?.toISOString() : null,
        endDate ? endDate?.toISOString() : null,
        type,
        branch,
        product,
        status,
      );
      return response;
    },
    onSuccess: (data) => {
      setReport(data);
      setPage(data?.page);
      setLimit(data?.limit);
      setTotalPages(data?.totalPages);
      setTotalItems(data?.total);
    },
    onError: (error) => {
      showError(error?.response?.data?.message || error.message);
    },
  });

  const { refetch, isFetching } = useQuery({
    queryKey: ["download reports"],
    queryFn: async () => {
      const response = await downloadGeneratedReport(
        page,
        limit,
        startDate ? startDate?.toISOString() : null,
        endDate ? endDate?.toISOString() : null,
        type,
        branch,
        product,
        status,
        true,
      );
      return response;
    },
    enabled: false,
    onSuccess: (data) => {
      downloadExportFile(data, `${type}.csv`);
    },
    onError: (error) => {
      showError(error?.response?.data?.message || error.message);
    },
  });

  const handleDownloadFile = () => {
    if (type) {
      refetch();
    } else {
      showError("No report type selected");
    }
  };

  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, totalItems);

  return (
    <>
      {openFilter && (
        <ReportFilter
          closeFilters={closeFilter}
          handleApplyFilter={closeFilter}
          fromDate={startDate}
          setFromDate={setStartDate}
          toDate={endDate}
          setToDate={setEndDate}
        />
      )}
      {isLoading ? (
        <div className="animate-pulse">
          {/* Table Skeleton */}
          <div className="bg-white border border-gray-200 rounded-md w-full mb-4">
            <div className="grid grid-cols-6 gap-4 p-3 border-b border-gray-200">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-4 bg-gray-200 rounded w-3/4"></div>
              ))}
            </div>
            {[...Array(20)].map((_, i) => (
              <div
                key={i}
                className="grid grid-cols-6 gap-4 p-3 border-b border-gray-100"
              >
                {[...Array(6)].map((_, j) => (
                  <div key={j} className="h-4 bg-gray-200 rounded w-3/4"></div>
                ))}
              </div>
            ))}
          </div>

          {/* Footer Skeleton */}
          <div className="flex justify-between items-center mt-4">
            <div className="flex items-center gap-4">
              <div className="h-4 w-20 bg-gray-200 rounded"></div>
              <div className="h-4 w-12 bg-gray-200 rounded"></div>
              <div className="h-4 w-16 bg-gray-200 rounded"></div>
            </div>
            <div className="h-8 w-40 bg-gray-200 rounded"></div>
          </div>
        </div>
      ) : (
        <div className="pl-[10px] w-full">
          <p className="text-[25px] my-[20px] font-bold text-[#222222]">
            <span
              className="cursor-pointer"
              onClick={() => navigate("/dashboard/reports")}
            >
              Reports & Analytics
            </span>{" "}
            {">"} <span>{formatLabel(name)}</span>
          </p>
          <div className="h-[70px] w-full bg-white flex items-center justify-between px-[10px]">
            <div className="flex items-center gap-[20px]">
              <button className="h-[40px] w-[117px] flex items-center justify-center gap-[5px] border border-[#00A676] bg-white rounded-[10px] text-[#00A676] font-medium text-[15px]">
                <Loader size={17} />
                Update
              </button>
              <div className="h-[40px] w-[117px] text-[#5C5C5C] font-medium text-[15px] rounded-[10px] border border-[#F3F3F3] bg-white flex items-center justify-center">
                0 Selected
              </div>
              <div
                onClick={toggleFilter}
                className="h-[40px] cursor-pointer w-[127px] text-[#5C5C5C] font-medium text-[15px] rounded-[10px] border border-[#F3F3F3] bg-[#FBF5F5] flex items-center justify-center gap-[5px]"
              >
                <SortAsc color="#000000" />
                <span className="text-[#000000]">Filter</span>
                <span className="text-[#000000]">0</span>
              </div>
            </div>
            <div className="flex items-center gap-[20px]">
              <p className="text-[#000000] truncate text-[15px] font-normal">
                {report?.data?.length ?? 0} Results
              </p>
              <Button
                onClick={handleDownloadFile}
                disabled={isFetching}
                className="flex items-center bg-[#F3F3F3] border-[0.7px] border-[#A8A8A8] h-[40px] rounded-[30px] px-[20px] justify-center gap-[5px] text-[16px] text-[#5C5C5C]"
              >
                {isFetching ? <Loading /> : <Upload size={17} />}
                <span>Export</span>
              </Button>
            </div>
          </div>
          <ReportTable tableData={report} />
          <div className="h-[50px] mb-[20px] flex items-center justify-between">
            <div className="flex items-center gap-[10px]">
              <p className="text-[15px] text-[#5C5C5C] font-normal">
                {start}–{end} of {totalItems}
              </p>
              <p className="text-[15px] text-[#5C5C5C] font-normal">
                Row/Page:
              </p>
              <p className="text-[15px] text-[#5C5C5C] font-normal">
                {page}/{totalPages}
              </p>
              <select
                className="h-[35px] w-[100px] px-[10px] text-[12px]"
                name="row"
                id="row"
                value={limit}
                onChange={(e) => setLimit(e.target.value)}
              >
                <option value="10">10 items</option>
                <option value="20">20 items</option>
                <option value="25">25 items</option>
              </select>
            </div>
            <Pagination
              currentPage={report?.page}
              totalPages={report?.totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default Report;
